// By path, not through the '@components' barrel: that barrel leads back to router.config, which
// imports this file - the cycle the note on flatAppRoutes below is about.
import { SharedSpinner } from '../Loading/SharedSpinner';
import { isAnyGranted, isDemoTenant, isFeatureEnabled } from '@lib';
import { isTokenExpired } from '@utils';
import { useMemo } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
// By path rather than through '@hooks': this module is loaded before anything else on screen,
// and the barrel would pull every hook - and the services behind them - into that first chunk.
import { useSessionExpiry } from '../../hooks/Helper/useSessionExpiry';
import { appRoutes, AppRoute } from './router.config';

declare const abp: any;

/**
 * Walks the route tree and returns every entry that has a `path`. Grouping
 * entries (Invoices, Inventory, Settings) carry only `children`, so they are
 * skipped while their descendants are kept.
 */
const flattenRoutes = (routes: AppRoute[]): AppRoute[] =>
  routes.flatMap((route) => [
    ...(route.path ? [route] : []),
    ...(route.children ? flattenRoutes(route.children) : []),
  ]);

/**
 * Resolved on first render rather than at module scope: router.config imports this file
 * while this file imports appRoutes from it, so reading appRoutes during module
 * initialisation hits the temporal dead zone. By render time both modules are ready.
 */
let flatAppRoutes: AppRoute[] | null = null;

const getFlatAppRoutes = () => {
  flatAppRoutes ??= flattenRoutes(appRoutes);
  return flatAppRoutes;
};

export const RequireAuth = () => {
  const location = useLocation();
  // Mounted once for the whole authenticated app, so the session is watched for as long as the
  // tab is open - not only when a route changes, which a tab left open all night never does.
  const sessionEnding = useSessionExpiry();
  const tokenExpired = isTokenExpired();

  const route = useMemo(
    () => getFlatAppRoutes().find((r: AppRoute) => matchPath(r.path as string, location.pathname)),
    [location.pathname]
  );

  // A session that ran out is already on its way to the login screen, with a message saying so.
  // Holding the screen here keeps the user from seeing a bare login form flash by first.
  if (sessionEnding) {
    return <SharedSpinner loading />;
  }

  if (tokenExpired || !abp.session.userId) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!abp.session.tenantId && location.pathname === '/') {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  if (route && route.permission && !isAnyGranted(route.permission)) {
    return (
      <Navigate
        to={abp.session.tenantId ? '/calendar' : '/dashboard'}
        state={{ from: location }}
        replace
      />
    );
  }

  const unavailable =
    route?.disabled ||
    (route?.feature && !isFeatureEnabled(route.feature)) ||
    (route?.paidOnly && isDemoTenant());

  if (unavailable) {
    return <Navigate to="/calendar" state={{ from: location }} replace />;
  }

  return <Outlet />;
};
